import type { ParseKeys, TFunction } from 'i18next';
import type {
  ManualDesignPoint,
  Objective,
  SimpleAlgorithmOverrides,
  OptimizationExtension,
  RangeSpec,
  Sobol,
  StudyDetailResponse,
} from '@/api/generated/types.gen';
export type { ManualDesignPoint } from '@/api/generated/types.gen';
import type { MeshMode, MeshResponse } from '@/api/generated/types.gen';

/**
 * Mint a FE-stable UUID for a new manual design point. Same identity across
 * autosave round-trips (the BE echoes verbatim and stamps it onto
 * `dp.inputs.manual_id` when the row materializes). Falls back to a
 * counter-based id on plain-HTTP dev origins where `crypto.randomUUID` is
 * secure-context only.
 */
let manualDpIdCounter = 0;
/** Cap on a draft's manual design points — the preview disables Add at it. */
export const MANUAL_DP_CAP = 100;

/**
 * One input a manual design point can run on, as the Add popover lists it: a
 * resolved mesh variant, or a geometry not meshed yet. Derived on the page from
 * the mesh queries, so the page mirrors it into the store for
 * `update_simops_draft` (ENG-2639).
 */
export interface ManualDpInputOption {
  /** Stable id matched against `ManualDesignPoint.input_id`. */
  id: string;
  /** Display label for the input picker. */
  label: string;
  /** Whether this variant's `id` resolves a mesh (CAD-in already meshed, or
   *  mesh-in) or is still geometry-keyed. Shown as a badge on the picker so
   *  the user knows which row they're targeting. */
  kind: 'geometry' | 'mesh';
  /** Resolved mesh id — shown abbreviated next to the filename when present
   *  (a geometry with multiple picked mesh variants shares one filename;
   *  the mesh id short-hash disambiguates in the picker). */
  meshId?: string | null;
}

export function newManualDpId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    return crypto.randomUUID();
  manualDpIdCounter += 1;
  return `manual-${manualDpIdCounter}`;
}

/**
 * A manual design point as the Add popover and `update_simops_draft` both write
 * it: no overrides is an absent key, and a blank label is null.
 */
export function manualDesignPoint(fields: {
  id: string;
  input_id: string;
  overrides?: ManualDesignPoint['overrides'];
  label?: string | null;
  color?: string | null;
}): ManualDesignPoint {
  const label = fields.label?.trim();
  return {
    id: fields.id,
    input_id: fields.input_id,
    overrides:
      fields.overrides && Object.keys(fields.overrides).length > 0
        ? fields.overrides
        : undefined,
    label: label ? label : null,
    ...(fields.color !== undefined ? { color: fields.color } : {}),
  };
}

/**
 * Split a user's label string into individual tag tokens. Delimited by
 * commas OR whitespace so "fast, open" and "fast open" both render two
 * chips. Empty tokens are dropped so a stray leading comma doesn't render
 * a blank pill. Consumed by StudyPreview + any other surface that surfaces
 * a manual DP's label as multiple chips.
 */
export function splitLabelTokens(label: string | null | undefined): string[] {
  if (!label) return [];
  return label
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter((s) => s !== '');
}
import { faceKeyAxisLabel } from '@/utils/externalFlowFaces';

// --- Solver settings — the discriminated union from the generated API ---

/**
 * Full union of solver-settings shapes the backend accepts, taken directly
 * from the generated OpenAPI types. Every member has `template` as a
 * required string literal (the discriminated-union members narrow it even
 * though the standalone class types mark it optional). Seven templates today:
 * `external_flow`, `internal_flow`, `urans`, `cht_steady`,
 * `thermal_conduction`, `structural_static`, `modal_frequency`.
 */
type RawSolverSettings = NonNullable<
  NonNullable<StudyDetailResponse['config']>['solver_settings']
>;

type ExternalFlowSolver = Extract<
  RawSolverSettings,
  { template: 'external_flow' }
>;

/**
 * `cht_steady` narrowed from the generated union. The union member already
 * has `template: 'cht_steady'` required; no local Omit/intersect dance
 * needed.
 */
export type ChtSteadyParams = Extract<
  RawSolverSettings,
  { template: 'cht_steady' }
>;

/**
 * Draft-store shape for `solver_settings`. The two CFD flow templates are
 * replaced by their FE-augmented drafts (which add `rho`/`mu`/`fluid_derived`
 * UI state); every other backend template flows through verbatim.
 */
export type SolverSettings =
  | CfdFlowConfig
  | Exclude<
      RawSolverSettings,
      { template: 'external_flow' } | { template: 'internal_flow' }
    >;

/**
 * Narrow to the FE-augmented `external_flow` draft (carries rho/mu/nu +
 * fluid_derived UI state). Deliberately still false for `internal_flow`,
 * which has its own sibling guard below — widening this one would hand
 * callers a `force_coeffs` field `InternalFlowParams` silently DROPS (that
 * model chain sets no `extra`, so pydantic ignores rather than rejects it).
 * A dropped field fails no validation and surfaces only as a missing result.
 */
export function isExternalFlowSolver(s: SolverSettings): s is SolverConfig {
  return s.template === 'external_flow';
}

/**
 * Narrow to the FE-augmented `internal_flow` draft. The sibling to
 * `isExternalFlowSolver`, not a widening of it: the two leaves differ by
 * `force_coeffs` (external only) and `inlet` / `flow_opening_roles`
 * (internal only), so one guard could not narrow to both.
 */
export function isInternalFlowSolver(
  s: SolverSettings,
): s is InternalFlowConfig {
  return s.template === 'internal_flow';
}

/**
 * Narrow to either FE-augmented CFD flow draft — for the surfaces that read
 * only shared fields (fluid properties, inlet velocity, sweeps). Anything
 * touching `force_coeffs` or `flow_opening_roles` needs the specific guard.
 */
export function isCfdFlowSolver(s: SolverSettings): s is CfdFlowConfig {
  return s.template === 'external_flow' || s.template === 'internal_flow';
}

/** Narrow to the `cht_steady` template. */
export function isChtSteadySolver(s: SolverSettings): s is ChtSteadyParams {
  return s.template === 'cht_steady';
}

/** Draft / wire structural_static leaf (template required by the union). */
export type StructuralStaticSolver = Extract<
  SolverSettings,
  { template: 'structural_static' }
>;

/** Narrow to the `structural_static` template. */
export function isStructuralStaticSolver(
  s: SolverSettings,
): s is StructuralStaticSolver {
  return s.template === 'structural_static';
}

/** Draft / wire thermal_conduction leaf with optional draft DOE axes. */
export type ThermalConductionSolver = Extract<
  SolverSettings,
  { template: 'thermal_conduction' }
>;

/** Narrow to the `thermal_conduction` template. */
export function isThermalConductionSolver(
  s: SolverSettings,
): s is ThermalConductionSolver {
  return s.template === 'thermal_conduction';
}

/** Draft / wire thermal_transient leaf with time schedule + IC (ENG-3002 / TH-6). */
export type ThermalTransientSolver = Extract<
  SolverSettings,
  { template: 'thermal_transient' }
>;

/** Narrow to the `thermal_transient` template. */
export function isThermalTransientSolver(
  s: SolverSettings,
): s is ThermalTransientSolver {
  return s.template === 'thermal_transient';
}

/** Either CalculiX thermal FEA draft (steady or transient). */
export type ThermalFeaSolver = ThermalConductionSolver | ThermalTransientSolver;

/** Narrow to either thermal FEA template. */
export function isThermalFeaSolver(s: SolverSettings): s is ThermalFeaSolver {
  return isThermalConductionSolver(s) || isThermalTransientSolver(s);
}

/** Narrow to the `modal_frequency` template. */
export function isModalFrequencySolver(
  s: SolverSettings,
): s is Extract<SolverSettings, { template: 'modal_frequency' }> {
  return s.template === 'modal_frequency';
}

/**
 * Templates that have an FEA result viewer (structural + modal). Narrower than
 * category === 'fea': thermal_conduction is FEA by category but uses
 * `showsFeaHeatmap` once it publishes `fea_heatmap` (ENG-2609).
 */
export function isFeaTemplate(template: string | null | undefined): boolean {
  return (
    template === 'structural_static' ||
    template === 'modal_frequency' ||
    template === 'linear_buckling'
  );
}

export function asExternalFlow(
  s: RawSolverSettings | null | undefined,
): ExternalFlowSolver | null {
  return s && 'template' in s && s.template === 'external_flow' ? s : null;
}

/**
 * Narrow to a CFD flow solver — either the external-flow FE-augmented
 * shape OR the raw internal-flow settings. Both templates share the
 * fields these summaries read (rho/mu/nu, inlet/outlet, overrides — NOT
 * force_coeffs, which is external-only), so the report + progress render
 * either one via
 * `SolverConfigSummary`. `asExternalFlow` alone was returning null for
 * internal-flow studies, leaving the running-study Study Configuration
 * card empty on any internal_flow run.
 */
export function asCfdFlow(
  s: RawSolverSettings | null | undefined,
): ExternalFlowSolver | null {
  if (!s || !('template' in s)) return null;
  return s.template === 'external_flow' || s.template === 'internal_flow'
    ? (s as ExternalFlowSolver)
    : null;
}

// --- Study config shape (matches study.config JSONB) ---

/**
 * A sweepable field value. Three shapes:
 * - `number[]` — discrete enumeration. Single-element = fixed, multi = DOE axis.
 * - `RangeSpec` — continuous range; only valid when surrogate modeling is on
 *   (drives Sobol sampling at the study level).
 * - `null` — field not set.
 */
export type SweepValue = number[] | RangeSpec | null;

export type { Objective, OptimizationExtension, RangeSpec, Sobol };

// --- Per-patch boundary condition types (mirrors backend OpenFoamBC union) ---

export type OpenFoamBCType =
  | 'velocity_inlet'
  | 'pressure_outlet'
  | 'wall_no_slip'
  | 'slip'
  | 'symmetry'
  | 'freestream';

/** Shared fields on every PatchBC variant. */
interface PatchBCBase {
  /** Canonical polyMesh patch name. Submit value — never shown in UI.
   *  Stable across role swaps; the renderer's face_index lookup and the
   *  solver's BC routing both key on this. */
  patch: string;
  /** Spatial data carried for geometry-sourced patches so buildSaveBody can
   *  emit face_tags in mesh_params (mesher needs these to create named patches). */
  centroid?: [number, number, number];
  normal?: [number, number, number];
  area?: number;
  face_index?: number | null;
  /** True once the user has explicitly chosen the BC `type` on this row.
   *  Auto-seed from `suggested_type` runs only when this is false, so a
   *  refetch (tab refocus, mesh swap on the same study, etc.) can't
   *  silently overwrite a user pick. */
  userOverridden?: boolean;
  /** Friendly display name shown in the BC row. Defaults to the BE's
   *  `user_label` (or canonical name when absent); auto-renames to the
   *  role default ("wall", "inlet", "outlet", …) when the user changes
   *  BC type — unless `displayNameUserOverridden` is set. */
  displayLabel?: string;
  /** True when the user typed a custom display name; locks `displayLabel`
   *  against the auto-rename on role-change effect. */
  displayNameUserOverridden?: boolean;
  /** True once the user typed a velocity directly on this row. Unedited
   *  rows follow the study-wide `inlet.velocity` (ENG-1031) — this flag
   *  exempts explicit per-patch values from that sync. */
  velocityUserOverridden?: boolean;
}

export type PatchBC =
  /**
   * No boundary condition chosen yet (ENG-1854).
   *
   * `null` rather than a new member of `OpenFoamBCType`: that union is the WIRE
   * type, and an `unset` member would be constructible in a request the backend
   * cannot accept. Being a distinct variant instead means `tsc` makes every
   * consumer answer for it rather than letting one fall through to a default.
   *
   * It must not reach the wire — the backend's `OpenFoamBC` is discriminated on
   * `type` and has no null member, so a draft autosave carrying one is a 422.
   * Every write path therefore filters through `isTypedPatchBC`; that is a
   * filter each site has to apply, NOT something this type guarantees, because
   * `buildSaveBody` reaches the wire behind an `as` cast that the compiler
   * cannot check.
   *
   * Seeded for a detected flow opening, which is a real boundary whose
   * DIRECTION nothing but the user can supply, so the backend deliberately
   * returns `needs_assignment` instead of a type (ENG-1497). Both available
   * guesses fail silently: a wall welds the port shut and the case still
   * converges, and a guessed direction can invert the flow. Note the row is
   * distinct from a patch the user deliberately SET to a wall — that one has
   * `type: 'wall_no_slip'` and `userOverridden`.
   */
  | (PatchBCBase & { type: null })
  | (PatchBCBase & {
      type: 'velocity_inlet';
      velocity: number;
      /** Optional velocity direction (raw, normalized at submit). Absent =
       *  inherit the study-wide inlet direction / flow_axis (ENG-1015). */
      direction?: [number, number, number] | null;
    })
  | (PatchBCBase & { type: 'pressure_outlet'; pressure: number })
  | (PatchBCBase & { type: 'wall_no_slip' })
  | (PatchBCBase & { type: 'slip' })
  | (PatchBCBase & { type: 'symmetry' })
  | (PatchBCBase & {
      type: 'freestream';
      velocity: number;
      pressure: number;
      /** Optional freestream velocity direction — same contract as
       *  velocity_inlet's. */
      direction?: [number, number, number] | null;
    });

/**
 * English source-of-truth labels — kept for callers that don't have a
 * translation context (typewise: tests, backwards-compat). New render sites
 * should use `bcTypeLabel(t, type)` so the label localizes.
 */
export const BC_TYPE_LABELS: Record<OpenFoamBCType, string> = {
  velocity_inlet: 'Velocity inlet',
  pressure_outlet: 'Pressure outlet',
  wall_no_slip: 'Wall (no-slip)',
  slip: 'Slip',
  symmetry: 'Symmetry',
  freestream: 'Freestream',
};

/**
 * i18n keys for the full BC-type labels — resolved via `bcTypeLabel(t, type)`.
 */
const BC_TYPE_LABEL_KEYS: Record<OpenFoamBCType, ParseKeys> = {
  velocity_inlet: 'simopsDraft.bc.velocityInlet',
  pressure_outlet: 'simopsDraft.bc.pressureOutlet',
  wall_no_slip: 'simopsDraft.bc.wallNoSlip',
  slip: 'simopsDraft.bc.slip',
  symmetry: 'simopsDraft.bc.symmetry',
  freestream: 'simopsDraft.bc.freestream',
};

/** Localized full BC-type label — "Velocity inlet" / "速度入口" / "速度インレット". */
export function bcTypeLabel(t: TFunction, type: OpenFoamBCType | null): string {
  if (type === null) return t('simopsDraft.bc.unset');
  return t(BC_TYPE_LABEL_KEYS[type]);
}

/**
 * Compact role labels for the BC-card subtext, where the full BC_TYPE_LABELS
 * ("Wall (no-slip)", "Velocity inlet") wrap onto two lines in the narrow draft
 * sidebar. The card already shows the velocity/pressure inputs + a descriptive
 * row name, so the subtext only needs the bare role. The BC-type dropdown still
 * uses the fuller BC_TYPE_LABELS, where the "(no-slip)" qualifier aids the pick.
 */
export const BC_TYPE_SHORT_LABELS: Record<OpenFoamBCType, string> = {
  velocity_inlet: 'Inlet',
  pressure_outlet: 'Outlet',
  wall_no_slip: 'Wall',
  slip: 'Slip',
  symmetry: 'Symmetry',
  freestream: 'Freestream',
};

const BC_TYPE_SHORT_LABEL_KEYS: Record<OpenFoamBCType, ParseKeys> = {
  velocity_inlet: 'simopsDraft.bc.short.inlet',
  pressure_outlet: 'simopsDraft.bc.short.outlet',
  wall_no_slip: 'simopsDraft.bc.short.wall',
  slip: 'simopsDraft.bc.short.slip',
  symmetry: 'simopsDraft.bc.short.symmetry',
  freestream: 'simopsDraft.bc.short.freestream',
};

/** Localized compact BC role label — "Inlet" / "入口" / "インレット". */
export function bcTypeShortLabel(
  t: TFunction,
  type: OpenFoamBCType | null,
): string {
  if (type === null) return t('simopsDraft.bc.short.unset');
  return t(BC_TYPE_SHORT_LABEL_KEYS[type]);
}

/**
 * Row colour / default-name key for a type that may be unset (ENG-1854).
 *
 * Unset returns `opening` rather than any BC type's key, so the row is not
 * tinted as whichever type it would have been defaulted to. Note `opening` is
 * NOT in `KNOWN_LABEL_INDEX`, so `labelToCssColor` hash-buckets it into the
 * shared custom slots: the swatch is stable per name but arbitrary, can collide
 * with a custom patch's colour, and matches nothing in the 3D view (an unset
 * face stays grey there — see `groupHasPaint`). Distinct from the role palette
 * is the property being relied on; a dedicated colour would need an entry in
 * that index.
 */
export function bcTypeDefaultLabel(type: OpenFoamBCType | null): string {
  return type === null ? 'opening' : BC_TYPE_DEFAULT_LABEL[type];
}

/**
 * Default friendly name to render in the BC row when the user changes
 * the BC type. Triggered by the auto-rename effect; suppressed when the
 * user has explicitly typed a custom `displayLabel`
 * (`displayNameUserOverridden`).
 */
export const BC_TYPE_DEFAULT_LABEL: Record<OpenFoamBCType, string> = {
  velocity_inlet: 'inlet',
  pressure_outlet: 'outlet',
  wall_no_slip: 'wall',
  slip: 'slip',
  symmetry: 'symmetry',
  freestream: 'farfield',
};

/**
 * Short, title-cased role word for an enclosure box face's default name —
 * combined with the face's axis ("-X"/"+Y"/…) to label all 6 wind-tunnel faces
 * consistently by their current role: "Wall -X", "Symmetry -Y", "Freestream +Z",
 * "Inlet -X", "Outlet +X". Distinct from `BC_TYPE_DEFAULT_LABEL` because that maps
 * freestream→`farfield` (the boundary TYPE); here the user-facing role word is
 * "Freestream".
 */
export const BC_TYPE_AXIS_BASE: Record<OpenFoamBCType, string> = {
  velocity_inlet: 'Inlet',
  pressure_outlet: 'Outlet',
  wall_no_slip: 'Wall',
  slip: 'Slip',
  symmetry: 'Symmetry',
  freestream: 'Freestream',
};

/**
 * Display name for an enclosure box patch: "<Role> <Axis>" derived from WHICH of
 * the 6 wind-tunnel faces it occupies (its FaceKey → axis) + its current BC type
 * — e.g. "Wall -X", "Inlet -Z", "Freestream +Y". Works for inlet/outlet (named
 * by role) and the wall_/farfield_<axis> laterals alike. Returns null for
 * non-enclosure patches (body_*, a merged "wall") so callers show the raw name.
 * Shared by the BC editor, the Study Configuration card, and the methodology
 * summaries so the relative-axis naming is identical everywhere.
 */
export function enclosureFaceLabel(
  patch: string,
  bcType: OpenFoamBCType,
  flowAxis: 'x' | 'y' | 'z',
): string | null {
  const axis = faceKeyAxisLabel(patch, flowAxis);
  return axis ? `${BC_TYPE_AXIS_BASE[bcType]} ${axis}` : null;
}

// --- BC role grouping (Boundary Conditions panel) ---

/**
 * Role buckets the BC panel groups patch rows under. Derived purely from each
 * patch's current `type`, so changing a patch's role moves it between groups,
 * the source group's count drops, and an emptied group stops rendering — the
 * "physics change reflected in the UI" the panel is built around. `slip` is a
 * free-slip wall, so it groups with walls.
 *
 * This keys on the exact OpenFOAM BC `type`. The complementary classifier
 * `matchFaceLabelRole` (`@/utils/faceLabelPalette`) infers a role from a
 * user-typed label substring (`"inlet_1"` → inlet) for face *coloring*. Keep
 * the two in sync if the role set ever grows (they're intentionally separate:
 * exact-type here vs fuzzy-label there).
 */
export type BCRoleGroup =
  | 'unset'
  | 'inlet'
  | 'outlet'
  | 'farfield'
  | 'symmetry'
  | 'wall';

export const BC_TYPE_GROUP: Record<OpenFoamBCType, BCRoleGroup> = {
  velocity_inlet: 'inlet',
  pressure_outlet: 'outlet',
  freestream: 'farfield',
  symmetry: 'symmetry',
  slip: 'wall',
  wall_no_slip: 'wall',
};

/**
 * What BC type a freshly-seeded row should carry (ENG-1854).
 *
 * The whole defect lives in this one decision, so it is a named function rather
 * than an expression inside the seed effect: it is the thing worth testing, and
 * the thing a future reader will want to find.
 *
 * - A detected flow opening gets `null`. The backend deliberately declines to
 *   type it (`needs_assignment`, ENG-1497) because only the user knows which way
 *   the flow goes, and both available guesses fail SILENTLY — a wall welds the
 *   port shut and the case still converges, a guessed direction can invert the
 *   flow.
 * - Anything else prefers the backend's own suggestion, and falls back to the
 *   name heuristic for legacy meshes that carry none.
 *
 * `openingNames` comes from `flowOpenings.openingPatchNames`, which the gates
 * read too — one answer to "is this patch a port", not two. The membership test
 * is done HERE rather than by the caller so it is covered by this function's
 * tests: keyed on the canonical `label`, while the heuristic fallback reads
 * `displayLabel`, and a caller that mixed the two would compile silently.
 */
export function seedBcTypeFor(
  patch: {
    label: string;
    displayLabel: string;
    suggestedType?: OpenFoamBCType;
  },
  openingNames: ReadonlySet<string>,
): OpenFoamBCType | null {
  if (openingNames.has(patch.label)) return null;
  return patch.suggestedType ?? labelToBCType(patch.displayLabel);
}

/**
 * Display names of the rows still awaiting a type — what a gate reports.
 *
 * Shared so the two gates (the draft Run gate and the BC-review resume) ask the
 * question the same way, through `isTypedPatchBC` rather than each spelling
 * `bc.type === null`. A second unset-like state then reaches both gates by
 * construction instead of slipping past them into the wire filter.
 */
export function unsetBcLabels(bcs: readonly PatchBC[]): string[] {
  return bcs
    .filter((bc) => !isTypedPatchBC(bc))
    .map((bc) => bc.displayLabel ?? bc.patch);
}

/** A row the user has actually answered — the only kind that may reach the wire.
 *
 *  Every submit path filters through this rather than casting, so adding a
 *  second unset-like state cannot silently start shipping. */
export function isTypedPatchBC(
  bc: PatchBC,
): bc is Exclude<PatchBC, { type: null }> {
  return bc.type !== null;
}

/**
 * The group of a row whose type may not be chosen yet (ENG-1854).
 *
 * A function rather than a widened `BC_TYPE_GROUP`, so that record stays keyed
 * on the WIRE types alone and every unset-aware caller goes through one place.
 * `unset` is its own group specifically so an unassigned opening does not sit in
 * the wall group: the whole defect was a detected port reading as a wall, and
 * grouping it with walls would reproduce that in the UI while the row model was
 * correct.
 */
export function bcTypeGroup(type: OpenFoamBCType | null): BCRoleGroup {
  return type === null ? 'unset' : BC_TYPE_GROUP[type];
}

/** Render order, top → bottom: the physics users tune first, walls last.
 *  `unset` leads — it is the only group holding a required, unanswered choice. */
export const BC_GROUP_ORDER: BCRoleGroup[] = [
  'unset',
  'inlet',
  'outlet',
  'farfield',
  'symmetry',
  'wall',
];

const BC_GROUP_LABEL_KEYS: Record<BCRoleGroup, ParseKeys> = {
  unset: 'simopsDraft.bc.groups.unset',
  inlet: 'simopsDraft.bc.groups.inlet',
  outlet: 'simopsDraft.bc.groups.outlet',
  farfield: 'simopsDraft.bc.groups.farfield',
  symmetry: 'simopsDraft.bc.groups.symmetry',
  wall: 'simopsDraft.bc.groups.wall',
};

/**
 * Localized BC group header — "Inlets" / "入口" / "入口".
 *
 * Keys rather than a hardcoded English record (`src/i18n/AGENTS.md`: strings go
 * in the locale bundles and components read them through `useTranslation`).
 * The old record shipped all six headers in English to every locale; the one
 * ENG-1854 adds is the header a ja/zh user has to ACT on, so it could not be
 * left raw, and translating one of six would have been the odd case out.
 */
export function bcGroupLabel(t: TFunction, group: BCRoleGroup): string {
  return t(BC_GROUP_LABEL_KEYS[group]);
}

export function labelToBCType(label: string): OpenFoamBCType {
  const l = label.toLowerCase();
  if (l.includes('inlet') || l.includes('upstream')) return 'velocity_inlet';
  if (l.includes('outlet') || l.includes('downstream'))
    return 'pressure_outlet';
  if (l.includes('symmetry')) return 'symmetry';
  if (l.includes('slip')) return 'slip';
  if (l.includes('farfield')) return 'freestream';
  return 'wall_no_slip';
}

interface MakePatchBCOptions {
  velocity?: number;
  pressure?: number;
  centroid?: [number, number, number];
  normal?: [number, number, number];
  area?: number;
  face_index?: number | null;
  /** Friendly text rendered in the BC row. Omit → row shows `patch`
   *  (canonical name) as fallback. Auto-renames on BC type change
   *  unless `displayNameUserOverridden` is set. */
  displayLabel?: string;
  /** Locks `displayLabel` against the auto-rename effect when the user
   *  has typed a custom name. */
  displayNameUserOverridden?: boolean;
  /** True once the user has explicitly chosen `type` — auto-seed from
   *  `suggested_type` skips entries where this is set. */
  userOverridden?: boolean;
  /** True once the user typed a velocity directly on this row (ENG-1031). */
  velocityUserOverridden?: boolean;
}

export function makePatchBC(
  patch: string,
  type: OpenFoamBCType | null,
  defaults?: MakePatchBCOptions,
): PatchBC {
  const meta: Partial<PatchBCBase> = defaults
    ? {
        ...(defaults.centroid && { centroid: defaults.centroid }),
        ...(defaults.normal && { normal: defaults.normal }),
        ...(defaults.area != null && { area: defaults.area }),
        ...(defaults.face_index != null && { face_index: defaults.face_index }),
        ...(defaults.displayLabel && { displayLabel: defaults.displayLabel }),
        ...(defaults.displayNameUserOverridden && {
          displayNameUserOverridden: true,
        }),
        ...(defaults.userOverridden && { userOverridden: true }),
        ...(defaults.velocityUserOverridden && {
          velocityUserOverridden: true,
        }),
      }
    : {};
  const spatial = meta;
  // No value fields: an unset row has nothing to carry, and seeding a velocity
  // or pressure here would make the row look answered.
  if (type === null) return { type, patch, ...spatial };
  switch (type) {
    case 'velocity_inlet':
      // 0 (not 1) so an untouched inlet reads as "not yet set" instead of a
      // silently-plausible 1 m/s (ENG-1015).
      return { type, patch, velocity: defaults?.velocity ?? 0, ...spatial };
    case 'pressure_outlet':
      return { type, patch, pressure: defaults?.pressure ?? 0, ...spatial };
    case 'wall_no_slip':
      return { type, patch, ...spatial };
    case 'slip':
      return { type, patch, ...spatial };
    case 'symmetry':
      return { type, patch, ...spatial };
    case 'freestream':
      return {
        type,
        patch,
        velocity: defaults?.velocity ?? 0,
        pressure: defaults?.pressure ?? 0,
        ...spatial,
      };
  }
}

export const isRange = (v: SweepValue | undefined): v is RangeSpec =>
  v != null && !Array.isArray(v) && (v as RangeSpec).kind === 'range';

export const isList = (v: SweepValue | undefined): v is number[] =>
  Array.isArray(v);

/**
 * The velocity a run would actually use for a velocity-bearing patch row
 * (ENG-1031): rows the user hasn't individually edited follow the
 * study-wide `inlet.velocity`; explicitly edited rows keep their own value.
 * Returns null for rows that carry no velocity. Shared by `buildSaveBody`
 * (wire reconciliation) and `useBcValidation` (zero-velocity gate) so the
 * two can't disagree.
 */
export function effectiveBcVelocity(
  bc: PatchBC,
  inletVelocity: SweepValue | undefined,
): number | null {
  if (bc.type !== 'velocity_inlet' && bc.type !== 'freestream') return null;
  if (bc.velocityUserOverridden) return bc.velocity;
  if (isList(inletVelocity) && inletVelocity.length > 0) {
    return inletVelocity[0];
  }
  return bc.velocity;
}

/**
 * Whether editing the velocity of BC `index` should write through to the
 * study-wide `inlet.velocity` instead of stamping a per-patch override.
 *
 * The backend treats study-wide `inlet.velocity` as authoritative — it drives
 * the internalField IC and is hard-gated `> 0` (`validate_runnable` /
 * `build_sim_case`), while a per-patch override only sets that patch's U. For
 * a SINGLE velocity inlet the two are the same physical number, so a per-patch
 * override strands the study-wide field at its 0 placeholder and the run is
 * rejected. Bind them: true only when the edited row is the sole
 * velocity-bearing inlet AND the study isn't sweeping velocity (study-wide is
 * a single-value list). Multi-inlet or a velocity sweep keep the override
 * model — there the per-patch value is a deliberate, non-collapsible exception.
 */
export function bindsSingleInletVelocity(
  bcs: readonly PatchBC[],
  index: number,
  studyWideVelocity: SweepValue | undefined,
): boolean {
  const target = bcs[index];
  const isVelocityBearing = (bc: PatchBC | undefined) =>
    bc?.type === 'velocity_inlet' || bc?.type === 'freestream';
  if (!isVelocityBearing(target)) return false;
  const velocityInletCount = bcs.filter(isVelocityBearing).length;
  if (velocityInletCount !== 1) return false;
  // Single-value list only: a multi-value list or RangeSpec is a sweep.
  return isList(studyWideVelocity) && studyWideVelocity.length === 1;
}

export type FlowType = 'laminar' | 'k_epsilon' | 'k_omega' | 'k_omega_sst';

export type FlowAxis = 'x' | 'y' | 'z';

/** Which fluid-property field is derived (computed from the other two). */
export type FluidDerived = 'rho' | 'mu' | 'nu';

// FluidPresetKey lives in @/data/fluid-presets (shared with MeshGen's BL
// y+ calculator). Re-exported here so existing SimOps imports keep working
// without a flag-day refactor.
export type { FluidPresetKey } from '@/data/fluid-presets';

/**
 * Everything the two CFD flow templates share. `external_flow` and
 * `internal_flow` differ by three fields only (see the two leaves below);
 * the rest — fluid properties, inlet/outlet, turbulence, overrides — is
 * identical. The shared consumers (`FluidPropertiesGroup`, the sweep
 * registry, `matrix.ts`) still take `SolverConfig`; retargeting them at this
 * base is 2b's work, once there is a second leaf to feed them.
 */
export interface CfdFlowConfigBase {
  flow_type: FlowType;
  /** Fluid density [kg/m³]. Linked to mu and nu via nu = mu/rho. */
  rho: number[];
  /** Dynamic viscosity [Pa·s]. Linked to rho and nu via nu = mu/rho. */
  mu: number[];
  /** Kinematic viscosity [m²/s]. Linked to rho and mu via nu = mu/rho. */
  nu: number[];
  /** Which of rho/mu/nu is auto-computed from the other two. */
  fluid_derived: FluidDerived;
  flow_axis: FlowAxis;
  inlet: {
    velocity: number[] | RangeSpec;
    /**
     * Study-wide inlet velocity direction (raw vector, normalized at
     * submit). null/absent = legacy behavior: backend derives direction
     * from `flow_axis` (ENG-1015).
     */
    direction?: [number, number, number] | null;
  } | null;
  outlet: { pressure: number[] | RangeSpec } | null;
  overrides: SimpleAlgorithmOverrides;
  /** Turbulence intensity I = u'/U [0–1]. Only used for RAS flow types. Default 0.05. */
  turbulence_intensity: number;
  /** Turbulent length scale L [m]. Only used for RAS flow types. null = backend default (0.1 m). */
  turbulent_length_scale: number | null;
}

/**
 * The FE-augmented `external_flow` draft. Adds the two fields the backend's
 * `ExternalFlowParams` has and `InternalFlowParams` does not.
 */
export interface SolverConfig extends CfdFlowConfigBase {
  template: 'external_flow';
  /**
   * Force coefficient reference geometry. null = feature disabled (no Cd/Cl/Cm in results).
   * Both fields must be non-null to send to the backend; partial state (one filled) stays null.
   *
   * Absent from `InternalFlowConfig`: per `InternalFlowParams`' docstring an
   * internal run has "no farfield, no lift/drag extraction", and that model
   * silently drops the field rather than rejecting it.
   */
  force_coeffs: {
    reference_area: number | null;
    reference_chord: number | null;
  } | null;
}

/**
 * The FE-augmented `internal_flow` draft.
 *
 * Two departures from `SolverConfig`, both mirroring `InternalFlowParams`:
 * `inlet` is required and non-null (the backend field carries no default, so
 * a draft must always seed one), and `flow_opening_roles` maps a patch to the
 * role it plays — see `missing_driven_flow_roles` in `sim_params.py` for how
 * a role counts toward inlet/outlet presence.
 */
export interface InternalFlowConfig extends CfdFlowConfigBase {
  template: 'internal_flow';
  inlet: NonNullable<CfdFlowConfigBase['inlet']>;
  flow_opening_roles: Record<string, 'inlet' | 'outlet'>;
}

/** Either FE-augmented CFD flow draft. */
export type CfdFlowConfig = SolverConfig | InternalFlowConfig;

/**
 * Trailing sentinel that `initDraftConfig` writes onto rehydrated filenames
 * when it doesn't yet know the real name. `useHydrateSelectedInputs` keys
 * off this to detect which entries still need a follow-up fetch.
 */
export const PLACEHOLDER_FILENAME_MARKER = '…';

/** A single picked geometry. */
export interface GeometrySelection {
  id: string;
  filename: string;
}

/** A single picked mesh, plus a back-pointer to its parent geometry. */
export interface MeshSelection {
  id: string;
  /** Parent geometry's filename, used as the display label. */
  filename: string;
  geometry_id: string;
}

/**
 * The "files" the user picks for a study. Mixed kinds aren't allowed — the
 * backend's `StudyInputs` carries `geometry_ids` *or* `mesh_ids`, so the
 * picker locks the user into one kind at a time. `null` = nothing picked.
 */
export type SelectedInputs =
  | { kind: 'geometry'; items: GeometrySelection[] }
  | { kind: 'mesh'; items: MeshSelection[] };

/**
 * SimOps-local copy of the MeshGen mesh-config fields, plus mesh sweeps
 * (e.g. the GCI element-size sweep). Kept as a copy rather than shared with
 * the global `toolSlice` so editing settings in SimOps doesn't leak into the
 * MeshGen workspace.
 *
 * Frontend-only for now — not yet persisted by the backend.
 */
export interface MeshSettings {
  // Base mesh config (mirrors fields from `toolSlice` / `MeshSettingsPanel`).
  element_type: 'tet' | 'hex' | 'poly';
  quality_preset: string;
  max_element_size: number;
  min_element_size: number;
  element_order: string;
  mesh_strategy: string | null;
  enclosure_enabled: boolean;

  // Sweep add-ons.
  /**
   * When enabled, the design-point matrix sweeps element size at the current
   * `max_element_size`, scaled by `gci_refinement_ratio` over `gci_levels`
   * design points. Sizes are derived at render time so they track edits to
   * `max_element_size`.
   */
  gci_enabled: boolean;
  gci_refinement_ratio: number;
  gci_levels: number;
}

/**
 * Backend-enforced floors for the GCI sweep (Celik et al. 2008, ASME V&V 20):
 * narrower ratios fall outside the asymptotic range, and Roache's safety
 * factor + observed-order solver both assume at least three grids. The form
 * uses these as the input `min` so the autosaved payload doesn't trip 422s.
 */
export const MIN_GCI_REFINEMENT_RATIO = 1.3;
export const MIN_GCI_LEVELS = 3;

/**
 * Default CHT params. `inlet`, `fluid_inlet_thermal`, and `regions` are
 * required by the type. `regions` must hold exactly one FluidRegionConfig and
 * ≥1 SolidRegionConfig (backend `_validate_regions`); keys are region labels —
 * the case writer resolves fluid/solid by config type, not by key, so the
 * 'fluid'/'solid' convention is safe for the single-solid case.
 */
export const DEFAULT_CHT_PARAMS: ChtSteadyParams = {
  template: 'cht_steady',
  flow_type: 'laminar',
  inlet: { velocity: [1] },
  fluid_inlet_thermal: { inlet_temperature: 293.15 },
  regions: {
    fluid: { fluid_material: 'water_20c' },
    solid: { material: 'aluminum_6061' },
  },
  boundary_conditions: [],
};

/**
 * Default structural-static draft. `boundary_conditions` lives on the params
 * (not the top-level OpenFOAM PatchBC list); the FEA BC panel edits it via
 * `patchSolverSettings`. `material` must resolve in STRUCTURAL_MATERIALS.
 * Leave BCs unset until MeshGen labels arrive. An explicit array (even empty)
 * is an authored draft and must not be reinitialized from mesh labels.
 */
export const DEFAULT_STRUCTURAL_STATIC_PARAMS: StructuralStaticSolver = {
  template: 'structural_static',
  material: 'steel_304',
};

/** Default modal-frequency draft. Eigenmode runs are loads-free. */
export const DEFAULT_MODAL_FREQUENCY_PARAMS: Extract<
  SolverSettings,
  { template: 'modal_frequency' }
> = {
  template: 'modal_frequency',
  material: 'steel_304',
  n_modes: 10,
};

/**
 * Default steady thermal-conduction draft. `boundary_conditions` lives on the
 * params (CalculiX face BCs); the thermal BC panel edits it via
 * `patchSolverSettings`. `material` must resolve in THERMAL_MATERIALS. Omit
 * BCs until MeshGen labels arrive — an explicit array (even empty) is an
 * authored draft and must not be reinitialized from mesh labels.
 */
export const DEFAULT_THERMAL_CONDUCTION_PARAMS: ThermalConductionSolver = {
  template: 'thermal_conduction',
  material: 'aluminum_6061',
};

/**
 * Default transient heat draft (ENG-3002 / TH-6). IC 300 K and a 60 s period
 * match the wire-only regressions; increment / output cadence stay unset so
 * the deck writer applies its TimeSchedule defaults (period/100 initial,
 * period/10 max, output = initial floored to ≤200 frames).
 */
export const DEFAULT_THERMAL_TRANSIENT_PARAMS: ThermalTransientSolver = {
  template: 'thermal_transient',
  material: 'aluminum_6061',
  initial_temperature_K: 300,
  time: { time_period_s: 60 },
};

/** Buckling requires a restraint and an applied load before submission. */
export const DEFAULT_LINEAR_BUCKLING_PARAMS: Extract<
  SolverSettings,
  { template: 'linear_buckling' }
> = {
  template: 'linear_buckling',
  material: 'steel_304',
  boundary_conditions: [],
  n_modes: 5,
};

/** Study method discriminator. Mirrors backend `StudyConfig.kind` (ENG-715). */
export type StudyKind = 'doe' | 'surrogate' | 'gci';

/** Study-wide automatic AI retry of failed cases (backend `auto_retry`). */
export interface AutoRetryConfig {
  enabled: boolean;
  /** Extra AI attempts per case on top of the original run (1-5). */
  max_attempts: number;
}

export const DEFAULT_AUTO_RETRY_MAX_ATTEMPTS = 2;

export interface StudyConfig {
  /**
   * Study method: 'doe' (default), 'surrogate', or 'gci'. Drives UI dispatch
   * and report layout. `mesh_sweep` is orthogonal — any kind may sweep mesh
   * resolution. Backend `_validate_kind` enforces that surrogate requires
   * `sampling` and gci requires `inputs.mesh_sweep`.
   */
  kind: StudyKind;
  /**
   * The geometries or meshes the study runs against. Mesh settings + sweep
   * add-ons only apply when the selection kind is `geometry` — picking
   * existing meshes skips meshing.
   */
  selected_inputs: SelectedInputs | null;
  /**
   * Per-geometry mesh picks for the mesh-in-draft flow. Mirrors the wire
   * `mesh_ids` field — persisted to the draft so the user's chosen mesh
   * survives reload. Empty = use the latest-completed mesh for each geometry
   * (the default). Only meaningful when `selected_inputs.kind === 'geometry'`.
   */
  meshPicks: string[];
  mesh_settings: MeshSettings;
  /**
   * Solver config — discriminated on `template`. Use `isExternalFlowSolver`
   * / `isChtSteadySolver` (above) to narrow at call sites that need the
   * template-specific fields, or `narrow(s, '<template>')` for any other
   * template in the registry.
   */
  solver_settings: SolverSettings;
  /**
   * Per-patch boundary conditions for internal flow. Each entry maps a mesh
   * patch name to a BC type (velocity_inlet, pressure_outlet, wall, etc.).
   * Populated from face_tags when the user selects an internal flow mesh.
   */
  boundary_conditions: PatchBC[];
  /**
   * Surrogate-model mode. Enables `RangeSpec` on supported sweep fields and
   * forces `sampling` to be set; the backend then draws design points from
   * the continuous space via the configured strategy (currently Sobol-only).
   */
  surrogate_enabled: boolean;
  /**
   * Sampling strategy applied to any `RangeSpec` axes. Required iff at least
   * one sweep field is a `RangeSpec`; ignored otherwise. We model only Sobol
   * in the UI today — LHS lives in the schema but is not yet wired.
   */
  sampling: Sobol | null;
  /**
   * Multi-objective Bayesian optimization continuation. Presence flips the
   * Study SFN into the propose → HITL → sim loop after iteration 0. v1
   * surface is `objectives` + `max_iterations` + `batch_q`. Null = DOE-only.
   */
  optimization: OptimizationExtension | null;
  /**
   * If a case fails mid-run, the AI reviews the failure and re-runs it with
   * adjusted solver settings. null = off. Persisted inside `config`.
   */
  auto_retry?: AutoRetryConfig | null;
  /**
   * Mesh AI auto-retry for study-driven meshing (ENG-1185): the backend
   * snapshots this into each spawned mesh's `mesh_params.auto_retry` at
   * creation (explicit per-mesh params win). Same {enabled, max_attempts}
   * shape as `auto_retry`, but max_attempts is bounded 1..3 (contracts
   * §1.1 — the UI options list enforces it, the backend re-clamps).
   * null = off. Persisted inside `config`; only meaningful on
   * geometry-kind studies.
   */
  mesh_auto_retry?: AutoRetryConfig | null;
  /**
   * User-authored design points appended to the sweep cartesian. Each entry
   * carries a sparse override map keyed by `sweep_axes()` dotted paths
   * (inlet.velocity / outlet.pressure / nu). Empty list = sweep-only. Cap
   * 100, enforced by BE `validate_runnable` and the dedicated POST /manual
   * endpoint. Persisted on `StudyConfig.extra_design_points` in DRAFT.
   */
  extra_design_points: ManualDesignPoint[];
}

/** Sobol's `n_samples` is meaningful at powers of two. UI snaps to these. */
export const SOBOL_SAMPLE_OPTIONS = [8, 16, 32, 64, 128, 256] as const;

export const DEFAULT_SOBOL: Sobol = {
  method: 'sobol',
  n_samples: 32,
  seed: 42,
  scramble: true,
};

/**
 * Objective count bounds for the backend `OptimizationExtension`. MOBO
 * requires ≥2 objectives — a single objective is degenerate (no Pareto
 * front), so the optimization block is omitted entirely until the user
 * authors a second one. The v1 ceiling stays at 4.
 */
export const MIN_OBJECTIVES = 2;
export const MAX_OBJECTIVES = 4;

/** Used when the user enables optimization but hasn't authored objectives. */
export const DEFAULT_OPTIMIZATION: OptimizationExtension = {
  objectives: [],
  max_iterations: 5,
  batch_q: 4,
};

export const DEFAULT_MESH_SETTINGS: MeshSettings = {
  element_type: 'tet',
  quality_preset: 'Medium',
  max_element_size: 10,
  min_element_size: 2,
  element_order: '1',
  mesh_strategy: null,
  enclosure_enabled: false,
  gci_enabled: false,
  gci_refinement_ratio: MIN_GCI_REFINEMENT_RATIO,
  gci_levels: MIN_GCI_LEVELS,
};

// Air at 20 °C, 1 atm.
export const DEFAULT_SOLVER_CONFIG: SolverConfig = {
  template: 'external_flow',
  flow_type: 'laminar',
  rho: [1.204],
  mu: [1.825e-5],
  nu: [1.5158e-5],
  fluid_derived: 'nu',
  flow_axis: 'x',
  // Seed an explicit 0 m/s so the velocity box shows a real value instead of
  // ghost placeholder text; users must set a speed before the run is
  // physically meaningful (ENG-1015).
  inlet: { velocity: [0] },
  outlet: { pressure: [0] },
  overrides: {},
  turbulence_intensity: 0.05,
  turbulent_length_scale: null,
  force_coeffs: null,
};

/**
 * Internal-flow draft defaults. Shares every fluid and turbulence value with
 * `DEFAULT_SOLVER_CONFIG` — the two templates differ only in the three fields
 * below, so seeding them apart would make a template switch change physics the
 * user never touched.
 *
 * `inlet` is a fresh object, not a reference to the external default's:
 * sharing it would let an edit through one draft mutate the other.
 */
export const DEFAULT_INTERNAL_FLOW_CONFIG: InternalFlowConfig = {
  template: 'internal_flow',
  flow_type: DEFAULT_SOLVER_CONFIG.flow_type,
  rho: [...DEFAULT_SOLVER_CONFIG.rho],
  mu: [...DEFAULT_SOLVER_CONFIG.mu],
  nu: [...DEFAULT_SOLVER_CONFIG.nu],
  fluid_derived: DEFAULT_SOLVER_CONFIG.fluid_derived,
  flow_axis: DEFAULT_SOLVER_CONFIG.flow_axis,
  // Seeded 0 m/s for the same reason as the external draft (ENG-1015): a real
  // value in the box beats ghost placeholder text. Required here, so there is
  // no null branch to fall back to.
  inlet: { velocity: [0] },
  outlet: { pressure: [0] },
  overrides: {},
  turbulence_intensity: DEFAULT_SOLVER_CONFIG.turbulence_intensity,
  turbulent_length_scale: DEFAULT_SOLVER_CONFIG.turbulent_length_scale,
  // Populated from the mesh's detected openings once the study picks a mesh;
  // an empty map is a study whose openings have not been assigned yet.
  flow_opening_roles: {},
};

// ---------------------------------------------------------------------------
// Template registry — one entry per solver template the backend accepts.
// ---------------------------------------------------------------------------
//
// Templates are the system's first-class solver discriminator. Each entry
// captures the relationship between a template and the rest of the
// pipeline: which mesh topology + element type the mesher must produce,
// which UI bucket the template falls into (the mesh authoring toggle
// today is 3-way: cfd/fea/cht), and the FE draft shape + defaults the
// SimOps form authors.
//
// Templates without `draft` set are wire-only: the backend accepts them
// but the FE has no form yet. Studies persisted with these templates
// rehydrate by passing the wire shape through verbatim; users see an
// `UnsupportedTemplatePanel` until the form ships.

/** Solver-template values the backend accepts. Sourced from the generated union. */
export type Template = RawSolverSettings['template'];

/**
 * Coarse UI categorization used by the mesh authoring toggle. The 3-way
 * cfd / fea / cht bucket is convenient for the mesh button strip but is
 * a derived value — the source of truth is the template.
 */
export type SimulationCategory = 'cfd' | 'fea' | 'cht';

/** Mesh modes (`mesh_params.domain.mode`) a given template is compatible with.
 * Re-exported, not restated: the hand-written copy that used to live here went
 * stale when ENG-2323 renamed the members, and nothing caught it until a call
 * site compared the two `MeshMode`s. */
export type { MeshMode };

export type MeshElementType = 'tet' | 'hex' | 'poly';

/** See `TemplateRegistryEntry.physics`. */
export type TemplatePhysics =
  | 'flow'
  | 'thermal'
  | 'stress'
  | 'modal'
  | 'buckling';

interface TemplateRegistryEntry<TDraft = unknown> {
  /** Which UI bucket this template falls into for the mesh authoring toggle. */
  category: SimulationCategory;
  /**
   * Mesh modes this template is valid against — single value or a list when
   * the template genuinely runs on several topologies (cht_steady takes both
   * CHT modes; the three FEA templates share the one `fea` mesh).
   *
   * These lists are what `templateForMeshMode` inverts, so a mode listed under
   * two DRAFTABLE templates is a mode the UI has to ask about. Adding one
   * casually turns an automatic choice back into a question.
   */
  meshMode: MeshMode | readonly MeshMode[];
  /** Element type the mesher must produce. `null` = no constraint. */
  elementType: MeshElementType | null;
  /**
   * What the run solves for, finer than `category` (ENG-3035): the FEA
   * bucket holds a heat problem, a stress problem, a frequency problem and a
   * load-factor problem, and copy that explains the run's outputs has to
   * name the right one — a peak stress means nothing on a modal run.
   */
  physics: TemplatePhysics;
  /**
   * FE draft shape and default value when this template is selected.
   * `null` for templates the FE doesn't author yet (wire-only — read-back
   * from persisted studies still works via the generated union).
   */
  draft: TDraft | null;
}

export const TEMPLATE_REGISTRY = {
  // `cfd_external` only. External flow solves the freestream around a body,
  // which needs the enclosure box a `cfd_internal` mesh doesn't have — the
  // farfield BCs the case writer emits then bind to patches that don't exist.
  // This entry used to accept both modes, which is how study
  // 54fa9e94-9ede-4e42-b507-842826118aa7 ran an internal mesh under the
  // external solver for 50 minutes without converging (ENG-2444).
  external_flow: {
    category: 'cfd',
    physics: 'flow',
    meshMode: 'cfd_external',
    elementType: null,
    draft: DEFAULT_SOLVER_CONFIG,
  },
  internal_flow: {
    category: 'cfd',
    physics: 'flow',
    meshMode: 'cfd_internal',
    elementType: null,
    draft: DEFAULT_INTERNAL_FLOW_CONFIG,
  },
  // Unsteady RANS. Backend-side only for now: no FE draft params are authored,
  // so `draft: null` keeps it out of every picker the way `thermal_conduction`
  // is kept out. The entry exists because `Template` is generated from the
  // backend's OpenAPI and this record must cover it — leaving it out breaks
  // the build the moment the client is regenerated, which the pre-commit hook
  // does on every commit.
  urans: {
    category: 'cfd',
    physics: 'flow',
    meshMode: ['cfd_external', 'cfd_internal'],
    elementType: null,
    draft: null,
  },
  cht_steady: {
    category: 'cht',
    physics: 'thermal',
    meshMode: ['cht_internal', 'cht_external'] as const,
    elementType: 'hex',
    draft: DEFAULT_CHT_PARAMS,
  },
  thermal_conduction: {
    category: 'fea',
    physics: 'thermal',
    meshMode: 'fea',
    elementType: 'tet',
    draft: DEFAULT_THERMAL_CONDUCTION_PARAMS,
  },
  // Transient heat (ENG-2975 / TH-2). Draft UI lands with TH-6 (ENG-3002).
  thermal_transient: {
    category: 'fea',
    physics: 'thermal',
    meshMode: 'fea',
    elementType: 'tet',
    draft: DEFAULT_THERMAL_TRANSIENT_PARAMS,
  },
  structural_static: {
    category: 'fea',
    physics: 'stress',
    meshMode: 'fea',
    elementType: 'tet',
    draft: DEFAULT_STRUCTURAL_STATIC_PARAMS,
  },
  modal_frequency: {
    category: 'fea',
    physics: 'modal',
    meshMode: 'fea',
    elementType: 'tet',
    draft: DEFAULT_MODAL_FREQUENCY_PARAMS,
  },
  linear_buckling: {
    category: 'fea',
    physics: 'buckling',
    meshMode: 'fea',
    elementType: 'tet',
    draft: DEFAULT_LINEAR_BUCKLING_PARAMS,
  },
} as const satisfies Record<Template, TemplateRegistryEntry>;

/**
 * Generic discriminated-union narrowing: `narrow(solver, 'cht_steady')`
 * returns the cht_steady member or null. Replaces bespoke `asExternalFlow`
 * helpers — one helper covers every template.
 */
export function narrow<T extends Template>(
  s: SolverSettings | RawSolverSettings | null | undefined,
  template: T,
): Extract<SolverSettings, { template: T }> | null {
  if (s == null) return null;
  return s.template === template
    ? (s as Extract<SolverSettings, { template: T }>)
    : null;
}

/**
 * Default FE draft for `template`, or null when the FE doesn't author this
 * template yet (caller falls back to whatever wire shape was persisted).
 */
export function defaultDraftFor(template: Template): SolverSettings | null {
  return (TEMPLATE_REGISTRY[template].draft as SolverSettings | null) ?? null;
}

/** Returns the mesh authoring UI bucket for a given template. */
export function categoryOf(template: Template): SimulationCategory {
  return TEMPLATE_REGISTRY[template].category;
}

/**
 * The template a mesh mode implies, or `null` when the mode doesn't pick one
 * on its own.
 *
 * Inverts `TEMPLATE_REGISTRY.meshMode` over the DRAFTABLE templates only:
 * `urans` carries `draft: null`, so it is wire-only and never a candidate.
 * That leaves exactly one ambiguous mode — `fea`, where structural_static,
 * modal_frequency, linear_buckling and thermal_conduction share the same tet
 * mesh and only the user knows which was meant. Every CFD and CHT mode
 * resolves to one template, so those studies never ask.
 */
export function templateForMeshMode(mode: MeshMode): Template | null {
  const matches = (Object.keys(TEMPLATE_REGISTRY) as Template[]).filter(
    (t) => defaultDraftFor(t) != null && meshModesOf(t).includes(mode),
  );
  return matches.length === 1 ? matches[0] : null;
}

/** Respect an explicit FEA analysis choice while correcting a CFD/CHT default. */
export function templateForSelectedMeshMode(
  mode: MeshMode,
  current: Template,
): Template | null {
  if (mode === 'fea') {
    return meshModesOf(current).includes(mode) ? current : 'structural_static';
  }
  return templateForMeshMode(mode);
}

/** Mesh modes `template` runs on, always as a list. */
export function meshModesOf(template: Template): readonly MeshMode[] {
  // `typeof`, not `Array.isArray`: the latter does NOT narrow the else branch
  // of a `readonly T[] | T` union, so a `modes === mode` there would still be
  // comparing a possible array to a string, unchecked by tsc.
  const modes = TEMPLATE_REGISTRY[template].meshMode;
  return typeof modes === 'string' ? [modes] : modes;
}

/**
 * The simulation mode a mesh was made for, or null for a legacy / directly
 * uploaded mesh that never recorded one. Mesh LIST rows carry `domain_mode`
 * and leave `mesh_params` out, so reading `mesh_params.domain.mode` alone saw
 * nothing on every list-sourced mesh (ENG-3100).
 */
export function meshModeOf(
  mesh: Pick<MeshResponse, 'domain_mode' | 'mesh_params'>,
): MeshMode | null {
  return mesh.domain_mode ?? mesh.mesh_params?.domain?.mode ?? null;
}

/** Whether a mesh can run under `template` — a mesh with no recorded mode
 *  gets the benefit of the doubt. */
export function meshFitsTemplate(
  mesh: Pick<MeshResponse, 'domain_mode' | 'mesh_params'>,
  template: Template,
): boolean {
  const mode = meshModeOf(mesh);
  return mode == null || meshModesOf(template).includes(mode);
}
